import { useCallback } from 'react'

/**
 * Controller that listens for clicks and determines if they occurred outside of
 * the editable cell. If it detect an intentional click outside, the handler is called.
 *
 * It was designed as a replacement for the `onBlur` event on the Tiptap editor.
 * It was too easily triggered when clicking draggable items or other UI.
 *
 * Note: It was initially a simple hook, but was generalized because it temporarily
 * also has to work in the <CellTypeSelect/>, so it can have a similar behaviour.
 * Once the other cell type becomes a custom node, this can be simplified.
 */
export function CellClickOutsideController({
  handler,
}: {
  handler: (event: MouseEvent | TouchEvent) => void
}) {
  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return

      const parentElm = node.parentElement
      if (!parentElm) return

      const listener = (event: MouseEvent | TouchEvent) => {
        if (!(event.target instanceof HTMLElement)) return

        const clickedInside = parentElm.contains(event.target as Node)
        const clickedDraggable = event.target.closest('[draggable="true"]') // Content nodes
        const clickedSuggestion = event.target.closest('.react-renderer') // Tiptap suggestions list
        const clickedToolbar = event.target.closest('[role="toolbar"]') // Cell toolbar
        const clickedOtherUI = event.target.closest('.backdrop-blur-xs') // Modal
        const clickedArkUI = event.target.closest('[data-scope]') // Any ArkUI component: popover, menu, tooltip, etc.

        // Special case for ArkUI dialog and backdrop
        const isArkUIDialogOpen = document.querySelector(
          '[data-scope="dialog"][data-state="open"]',
        )

        if (
          !clickedInside &&
          !clickedDraggable &&
          !clickedToolbar &&
          !clickedSuggestion &&
          !clickedOtherUI &&
          !clickedArkUI &&
          !isArkUIDialogOpen
        ) {
          handler(event)
        }
      }

      document.addEventListener('mousedown', listener)
      document.addEventListener('touchstart', listener)

      return () => {
        document.removeEventListener('mousedown', listener)
        document.removeEventListener('touchstart', listener)
      }
    },
    [handler],
  )

  return <div ref={refCallback}></div>
}
